import React, { useEffect, useState } from 'react';
// import { useTranslation } from 'react-i18next';

import { dayjs, PlusIcon, ShortUser } from '@umanforge/common';
import { ChronogramHeader } from 'features/chronogram/components/chronogramHeader';
import { CALENDAR_CONST } from 'constants/calendar.constants';
import { DateLocaleService } from 'services/dateLocale.service';
import { Column } from 'services/chronogram.service';
import { TimeGridRecurrenceItem } from 'interfaces/timegridRecurrence.interface';
import { TimegridCalendarLineHeader } from './timegridCalendarLineHeader';
import { TimegridCalendarTile } from './timegridCalendarTile';
import { useTranslation } from 'react-i18next';
import DateSelector from 'components/inputs/dateSelector';

interface TimeGridCalendarProps {
  sectorId: string;
  users: ShortUser[];
  timegridRecurrences: TimeGridRecurrenceItem[];
  onTimeGridClick: ( timeGridRecurrence: TimeGridRecurrenceItem, usersId?: string[] ) => void;
  isWeekEnd: boolean;
  currentDay: dayjs.Dayjs
  setCurrentDay: ( day: dayjs.Dayjs ) => void
  resetUsersSelected?: boolean;
}

export interface BreaktimeError {
  endHours: string | undefined,
  duration: string | undefined,
}

const TimeGridCalendar: React.FC<TimeGridCalendarProps> = ( {
  timegridRecurrences = [],
  users,
  onTimeGridClick,
  isWeekEnd,
  currentDay,
  setCurrentDay,
  resetUsersSelected = false
} ) => {
  const { t } = useTranslation();

  const columns: Column[] = [];
  const range = ( isWeekEnd ? CALENDAR_CONST.WEEKDAYS : CALENDAR_CONST.WEEKDAYSWITHOUTWEEKEND );
  const offset = 1;

  const [ timegridByUser, setTimegridByUser ] = useState<Map<string, TimeGridRecurrenceItem[]>>( new Map<string, TimeGridRecurrenceItem[]>() );
  const [ selected, setSelected ] = useState<Set<string>>( new Set() );
  const [ allSelected, setAllSelected ] = useState<boolean>( false );


  useEffect( () => {
    const tgrByUser = new Map<string, TimeGridRecurrenceItem[]>();
    if ( timegridRecurrences.length > 0 ) {
      timegridRecurrences.forEach( tgr => {
        const tgrs = tgrByUser.get( tgr.idUser ) ?? [];
        tgrByUser.set( tgr.idUser, [ ...tgrs, tgr ] );
      } );
    }
    setTimegridByUser( tgrByUser );
  }, [ timegridRecurrences ] );

  useEffect( () => {
    setAllSelected( users.length > 0 && selected.size === users.length );
  }, [ selected, users ] );

  useEffect( () => {
    setSelected( new Set() );
  }, [ resetUsersSelected ] );


  for ( let i = 0; i < range; i++ ) {
    const day = currentDay.startOf( 'week' ).add( i, 'day' );
    columns.push( {
      value: i,
      name: DateLocaleService.displayDate( day, 'ddd DD' ),
      rawValue: day.toISOString(),
      subColumns: []
    } );
  }

  const findTimeGridRecurrences = ( tgrs: TimeGridRecurrenceItem[], value: number ): TimeGridRecurrenceItem[] => {
    return tgrs.filter( tgr => {
      let tgrVal = dayjs( tgr.date ).day() as number;
      if ( tgrVal == 0 ) { tgrVal = 7; }
      return tgrVal == value;
    } ).sort( ( a, b ) => {
      return b.priority - a.priority;
    } );
  };

  const toggleAll = () => {
    setSelected( allSelected ? new Set() : new Set( users.map( u => {return u.id;} ) ) );
  };

  const toggleOne = ( id: string ) => {
    setSelected( prev => {
      const next = new Set( prev );
      if ( next.has( id ) ) { next.delete( id ); } else { next.add( id ); }
      return next;
    } );
  };


  const calendarCell = ( column: Column, i: number, timegridRs: TimeGridRecurrenceItem[], user: ShortUser ): JSX.Element => {
    const inFuture = dayjs( column.rawValue ).isAfter( dayjs().startOf( 'day' ) );
    const haveTimegridRecurrence = ( timegridRs != null && timegridRs.length > 0 );
    const baseClassName = 'relative flex items-center justify-center text-sm text-gray-400 border-l border-slate-400/40 border-dashed group';

    if ( inFuture ) {

      // Future days have timegrid recurrence
      return haveTimegridRecurrence ?
        <div key={ column.name + '_' + i } className= { baseClassName + ' hover:justify-start'}>
          <div className={'h-full w-full relative flex flex-col items-start justify-start bg-green-50/50 hover:bg-green-100 overflow-x-hidden px-2' } >
            { timegridRs.map( ( tgr, idx ) => {
              return <TimegridCalendarTile
                key={tgr.id + '_' + idx}
                timegridRecurrence={tgr}
                timeGrid={ tgr.timeGrid }
                onClick={ onTimeGridClick }
                className={ 'w-full z-10 ' + ( idx == 0 ? 'border border-blue-500' : 'opacity-70' ) }
              />;
            } ) }
            <div
              className="font-texte text-sm text-gray-600 place-self-center h-full flex cursor-pointer invisible w-0 group-hover:py-3 group-hover:w-fit group-hover:visible hover:text-gray-800"
              onClick={ () => { onTimeGridClick( { date: column.rawValue, idUser: user.id } as TimeGridRecurrenceItem ); }}
            >
              <PlusIcon className='w-0 h-full group-hover:w-4'/>
              <p className={ 'justify-center ml-2 content-center transition duration-150 ease-in-out w-full opacity-0 invisible fixed group-hover:opacity-100 group-hover:visible group-hover:relative' } >{ t( 'translation:add_timegrid' ) }</p>
            </div>
          </div>
        </div>
        :

      // Future days have no timegrid recurrence
        <div key={ column.name + '_' + i } className= { baseClassName + ' hover:justify-start'}>
          <div
            className={'h-full w-full relative flex items-center justify-center bg-green-50/50 hover:bg-green-100 py-1 cursor-pointer' }
            style={{ gridColumnStart: i + 1, gridColumnEnd: ( i + 2 ) }}
            onClick={ () => { onTimeGridClick( { date: column.rawValue, idUser: user.id } as TimeGridRecurrenceItem ); }}
          >
            <div className="font-texte text-sm text-gray-600 place-self-center w-fit h-fit flex">
              <PlusIcon className='w-4'/>
              <p className={ 'justify-center ml-2 content-center transition duration-150 ease-in-out w-full opacity-0 invisible fixed group-hover:opacity-100 group-hover:visible group-hover:relative' } >{ t( 'translation:add_timegrid' ) }</p>
            </div>
          </div>
        </div>;
    }
    else {

      // Past days have timegrid recurrence
      return haveTimegridRecurrence ?
        <div key={ column.name + '_' + i } className= { baseClassName }>
          <div className={'h-full w-full relative flex flex-col items-start justify-start overflow-x-hidden px-2' } >
            { timegridRs.map( ( tgr, idx ) => {
              return <TimegridCalendarTile
                key={tgr.id + '_' + idx}
                timegridRecurrence={tgr}
                timeGrid={ tgr.timeGrid }
                onClick={ onTimeGridClick }
                className={ 'w-full z-10 ' + ( idx == 0 ? 'border border-blue-500' : 'opacity-50' ) }
              />;
            } ) }
          </div>
        </div>
        :

      // Past days have no timegrid recurrence
        <div key={ column.name + '_' + i } className= { baseClassName }>
          <p>{ t( 'translation:no_allocation' ) }</p>
        </div>;
    }
  };



  // Render
  return (
    <div>
      <div className='grid grid-flow-col grid-cols-3 self-justify-end'>
        <div/>
        <DateSelector
          setCurrentDay={ setCurrentDay }
          currentDay={ currentDay }
          isWeek={ true }
        />
      </div>
      <div className='relative mt-3'>

        <div className={ `absolute bottom-full left-0 mb-1 z-10 flex items-center gap-3 px-4 overflow-hidden transition-all duration-200 rounded-xl shadow-md ${ selected.size > 0 ? 'h-11 py-2 bg-app-color-50 border border-app-color-200' : 'h-0 py-0 border-transparent' }` }>
          <span className="text-sm text-gray-600 whitespace-nowrap">
            { selected.size } { selected.size === 1 ? t( 'translation:selected_item' ) : t( 'translation:selected_items' ) }
          </span>
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-white bg-app-color hover:bg-app-color-light rounded-lg transition-colors whitespace-nowrap"
            onClick={ () => {
              const ids = Array.from( selected );
              if ( ids.length === 0 ) { return; }
              onTimeGridClick( { date: dayjs().add( 1, 'day' ).toISOString(), idUser: '' } as TimeGridRecurrenceItem, structuredClone( ids ) );
            } }
          >
            <PlusIcon className="w-3.5 h-3.5 fill-white shrink-0" />
            { t( 'translation:allocate' ) }
          </button>
        </div>
        <div className="overflow-x-auto overflow-y-visible">
          <div className="min-w-full">

            <ChronogramHeader
              columns={columns}
              center={ false }
              offset={offset}
              offsetChildren={
                <div className='relative w-full h-full'>
                  <div className='m-1 ml-3 grid grid-cols-2 items-center py-0.5 px-2 cursor-pointer' onClick={ toggleAll } >
                    <input
                      type="checkbox"
                      checked={ allSelected }
                      className="accent-app-color-500 w-4 h-4 cursor-pointer"
                    />
                    <p className='text-gray-500 font-medium whitespace-nowrap'>{ t( 'translation:select_all' ) }</p>
                  </div>
                </div>
              }
            />

            { users.map( ( user, index ) => {
              return <div
                className={ 'grid w-200 border border-gray-200 border-t-0 relative even:bg-[#bfdbfe65] bg-[#bfdbfe25]'}
                style={{ gridTemplateColumns: `repeat(${columns.length + offset}, minmax(0, 1fr))` }}
                key={ `_chrono_line_${index}_${user.id}` }
              >
                <TimegridCalendarLineHeader
                  offset={offset}
                  lineName={ user.fullname ?? 'time_grid' }
                  onClick={ () => { toggleOne( user.id ); } }
                  checkboxComponent={
                    <div className="py-2 mr-2" onClick={ e => { e.stopPropagation(); } }>
                      <input
                        type="checkbox"
                        checked={ selected.has( user.id ) }
                        onChange={ () => { toggleOne( user.id ); } }
                        className="accent-app-color-500 w-4 h-4 cursor-pointer"
                      />
                    </div>
                  }
                />

                <div className="flex relative flex-row" style={{ gridColumn: `span ${columns.length}` }} key={ 'timegrid_recurrence_tile' + user.id + '_' + index}>
                  <div className={'grid relative w-full h-full'} style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }} >
                    { columns.map( ( column, i ) => {
                      const timegridRs = findTimeGridRecurrences( timegridByUser.get( user.id ) ?? [], i + 1 );
                      return calendarCell( column, i, timegridRs, user );
                    } ) }
                  </div>
                </div>
              </div>; } ) }
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeGridCalendar;
